/*
 * SPDX-FileCopyrightText: © 2025 Schneider Electric
 *
 * SPDX-License-Identifier: Apache-2.0
 */

:host {
  display: block;
}

:host(:focus-visible) {
  outline: var(--qds-theme-focus-border) solid var(--qds-focus-border-width);
  outline-offset: var(--qds-focus-border-offset);
  border-radius: var(--qds-focus-border-radius);
}

.qds-icon-button {
  display: flex;
  position: relative;

  /* Avoid automatic minimum size on flex element: https://www.w3.org/TR/css-flexbox-1/#min-size-auto */
  min-width: 0;
  align-items: center;
  justify-content: center;
  border-radius: var(--qds-control-border-radius);
  border: none;
  appearance: none;
  user-select: none;
  cursor: pointer;
  box-sizing: border-box;
  background-color: initial;
  color: var(--qds-theme-control-text-standard);
  padding-inline: 0;
  line-height: normal;

  &:hover {
    background-color: var(--qds-theme-interactive-background-hover);
  }

  &:active {
    background-color: var(--qds-theme-interactive-background-pressed);
  }
}

.qds-disabled {
  opacity: var(--qds-theme-disabled);
  pointer-events: none;
}

.qds-icon {
  flex-shrink: 0;
}

.qds-indicator {
  position: absolute;
  inset-block-start: 0;
  inset-inline-end: 0;
  transform: translate(50%, -50%);
}

[data-size='small'] {
  min-height: var(--qds-control-small-height);
  width: var(--qds-control-small-height);

  &.qds-icon {
    min-height: var(--qds-control-small-icon-size);
    width: var(--qds-control-small-icon-size);
    font-size: var(--qds-control-small-icon-size);
  }
}

[data-size='standard'] {
  min-height: var(--qds-control-standard-height);
  width: var(--qds-control-standard-height);

  &.qds-icon {
    min-height: var(--qds-control-standard-icon-size);
    width: var(--qds-control-standard-icon-size);
    font-size: var(--qds-control-standard-icon-size);
  }
}

[data-size='large'] {
  min-height: var(--qds-control-large-height);
  width: var(--qds-control-large-height);

  &.qds-icon {
    min-height: var(--qds-control-large-icon-size);
    width: var(--qds-control-large-icon-size);
    font-size: var(--qds-control-large-icon-size);
  }
}

[data-size='hero'] {
  min-height: var(--qds-control-hero-height);
  width: var(--qds-control-hero-height);
  border-radius: var(--qds-control-hero-border-radius);

  &.qds-icon {
    font-size: var(--qds-control-hero-icon-size);
    min-height: var(--qds-control-hero-icon-size);
    width: var(--qds-control-hero-icon-size);
  }
}

[data-importance='subdued'] {
  color: var(--qds-theme-control-text-standard);

  &:hover {
    background-color: var(--qds-theme-interactive-background-hover);
  }

  &:active {
    background-color: var(--qds-theme-interactive-background-pressed);
  }
}

[data-importance='standard'] {
  color: var(--qds-theme-control-text-standard);
  border: var(--qds-control-border-width) solid var(--qds-theme-control-border);

  &:hover {
    background-color: var(--qds-theme-interactive-background-hover);
  }

  &:active {
    background-color: var(--qds-theme-interactive-background-pressed);
  }
}

[data-importance='emphasized'] {
  background-color: var(--qds-theme-signature-color-default);
  color: var(--qds-theme-signature-color-contrast);

  &:hover {
    background-color: var(--qds-theme-signature-color-hover);
  }

  &:active {
    background-color: var(--qds-theme-signature-color-pressed);
  }
}

[data-importance='destructive'] {
  background-color: var(--qds-theme-feedback-action-destructive-default);
  color: var(--qds-theme-feedback-action-destructive-contrast);

  &:hover {
    background-color: var(--qds-theme-feedback-action-destructive-hover);
  }

  &:active {
    background-color: var(--qds-theme-feedback-action-destructive-pressed);
  }
}

[data-importance='subdued-destructive'] {
  color: var(--qds-theme-feedback-action-destructive-default);
  border: none;

  &:hover {
    background-color: var(--qds-theme-interactive-background-hover);
    color: var(--qds-theme-feedback-action-destructive-default);
  }

  &:active {
    background-color: var(--qds-theme-interactive-background-pressed);
    color: var(--qds-theme-feedback-action-destructive-default);
  }
}
